import React from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * PaginationControls — consistent page-size selector + prev/next paginator.
 *
 * Props:
 *  - page: 0-indexed current page number
 *  - pageSize: current page size (rows per page)
 *  - totalCount: total number of matching rows
 *  - onPageChange(newPage): callback when user clicks prev/next
 *  - onPageSizeChange(newSize): callback when user picks a new page size
 *  - options: array of page-size options (defaults to [10, 50, 100, 500])
 *  - label: string label e.g. "entrepreneurs", "staff"
 */
const PaginationControls = ({
  page = 0,
  pageSize = 50,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  options = [10, 50, 100, 500],
  label = 'records',
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startRow = totalCount === 0 ? 0 : page * pageSize + 1;
  const endRow = Math.min(totalCount, (page + 1) * pageSize);
  const canPrev = page > 0;
  const canNext = (page + 1) * pageSize < totalCount;

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">Rows per page:</span>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => {
            const n = parseInt(v, 10);
            onPageSizeChange && onPageSizeChange(n);
          }}
        >
          <SelectTrigger className="w-24 h-8" data-testid="page-size-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map((n) => (
              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-slate-600" data-testid="pagination-range">
        Showing <b>{startRow.toLocaleString('en-IN')}</b>–<b>{endRow.toLocaleString('en-IN')}</b>
        {' of '}
        <b>{totalCount.toLocaleString('en-IN')}</b> {label}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => canPrev && onPageChange(page - 1)}
          disabled={!canPrev}
          data-testid="pagination-prev"
          className="h-8"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Prev
        </Button>
        <span className="text-xs text-slate-500 px-2">
          Page <b className="text-slate-700">{page + 1}</b> / {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => canNext && onPageChange(page + 1)}
          disabled={!canNext}
          data-testid="pagination-next"
          className="h-8"
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default PaginationControls;
